import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { LanguageToggle } from "@/components/i18n/LanguageToggle";
import { useTranslation } from "@/i18n/I18nProvider";
import { citySlug, DEFAULT_CITY } from "@/lib/cities";
import { usePageMeta } from "@/lib/pageMeta";

/**
 * The custom 404 (seo-geo.md Rule 13): any unknown path, an unknown city, or a
 * venue URL whose venue is gone or hidden. Bilingual, noindex, and two ways
 * back in: the Cluj feed (where the venues are) and the city gate.
 */
export default function NotFound() {
  const { t } = useTranslation();
  usePageMeta({ title: t("META_NOT_FOUND_TITLE"), noindex: true });

  return (
    <main className="app-ambient grid min-h-[100dvh] place-items-center bg-background px-4 py-16 text-foreground">
      <div className="fixed right-3 sm:right-5" style={{ top: "max(0.75rem, env(safe-area-inset-top, 0px))" }}>
        <LanguageToggle inline />
      </div>
      <div className="w-full max-w-md text-center">
        <Link to="/" aria-label="Resto" className="mx-auto inline-flex">
          <img src="/brand/resto-icon.svg" alt="" width={56} height={56} className="h-14 w-14" />
        </Link>
        <p className="mt-8 font-mono text-xs uppercase tracking-[0.28em] text-pulse">404</p>
        <h1 className="mt-3 font-display text-[2rem] leading-[1.05] tracking-tight text-zinc-50 light:text-zinc-900 sm:text-4xl">
          {t("NOT_FOUND_TITLE")}
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-pretty text-sm leading-relaxed text-zinc-300 light:text-zinc-700">
          {t("NOT_FOUND_BODY")}
        </p>
        <div className="mt-8 flex flex-col items-stretch gap-2.5 sm:flex-row sm:justify-center">
          <Link
            to={`/${citySlug(DEFAULT_CITY)}`}
            className="group inline-flex items-center justify-center gap-2 rounded-xl bg-paper light:bg-ink px-4 py-2.5 text-sm font-semibold text-ink light:text-paper shadow-[0_4px_18px_-6px_rgba(245,241,232,0.35)] transition hover:bg-white light:hover:bg-zinc-800"
          >
            {t("NOT_FOUND_CITY")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-xl border border-white/15 light:border-black/15 px-4 py-2.5 text-sm font-medium text-zinc-200 light:text-zinc-800 transition hover:border-white/40 light:hover:border-black/35"
          >
            {t("NOT_FOUND_HOME")}
          </Link>
        </div>
      </div>
    </main>
  );
}
